<template>
    <div class="music-player-container">
        <div
            v-for="(sample, indx) in songs"
            :key="sampleKey(sample)"
            class="hidden-sample"
            style="display: none"
        >
            <audio
                ref="sample-music"
                :data-key="indx"
                @loadstart="() => handleLoadMusic(sample, indx)"
                @canplaythrough="handleAddDuration"
                controls
            >
                <source :src="musicSource(sample)" />
            </audio>
        </div>
        <div
            v-if="mode === 'custom' && player.length"
            :style="styles.container"
            class="player-container"
        >
            <slot :player="player" :controls="{ handlePlay, handlePause }"></slot>
        </div>
        <div v-else :style="styles.container" class="player-container">
            <div
                v-for="(song, indx) in player"
                :key="song.src + indx"
                :style="styles.player"
                class="player-wrapper"
            >
                <div class="play-action">
                    <button
                        v-if="!song.playing"
                        @click="handlePlay(song, indx)"
                        class="play-btn"
                    >
                        <img :src="URLS.icons.play" alt="play-btn" />
                    </button>
                    <button v-else @click="handlePause(song)" class="pause-btn">
                        <img :src="URLS.icons.pause" alt="pause-btn" />
                    </button>
                </div>
                <div v-if="!song.editing" class="song-info">
                    <p v-if="mode !== 'card-view'">
                        {{ `${renderSongName(song)} - ${song.duration}` }}
                    </p>
                    <p v-else>
                        {{ trackNumber(indx) }}
                    </p>
                    <div v-if="song.progress && mode !== 'card-view'" class="progress">
                        <div
                            class="progress-bar"
                            :style="{ width: `${song.progress}%` }"
                            :aria-valuenow="song.progress"
                            role="progressbar"
                            aria-valuemin="0"
                            aria-valuemax="100"
                        ></div>
                    </div>
                </div>
                <div v-else class="song-info">
                    <input
                        v-model="song.newName"
                        @keydown.enter="handleChangeName(song, indx)"
                        type="text"
                        placeholder="Enter new name"
                    />
                    <button @click="handleChangeName(song, indx)">Ok</button>
                </div>
                <div v-if="mode === 'edit'" class="kebab-menu">
                    <button @click="handleToggleMenu(song)">
                        <img :src="URLS.icons.kebab" alt="kebab-menu" />
                    </button>
                </div>
                <div v-if="song.showMenu" class="music-menu">
                    <button @click="handleClickEdit(song)">Edit sound</button>
                    <button @click="handleClickMove(song, indx)">Move</button>
                    <div class="menu-divider"></div>
                    <button @click="handleClickRemove(indx)">Remove</button>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import { useMusicPlayer } from '@/composables/ui/useMusicPlayer'

    const URLS = {
        icons: {
            play: '/images/icons/audio-play.svg',
            pause: '/images/icons/audio-pause.svg',
            kebab: '/images/icons/kebab-menu.svg'
        }
    }
    export default {
        name: 'MusicPlayer',
        props: {
            mk: {
                required: false,
                type: String,
                default: 'player'
            },
            songs: {
                required: true,
                type: Array
            },
            mode: {
                required: false,
                type: String,
                default: 'edit',
                validator: (v) => ['edit', 'view', 'card-view', 'custom'].includes(v)
            },
            styles: {
                required: false,
                type: Object,
                default: () => ({})
            }
        },
        setup(props) {
            const { players, player } = useMusicPlayer(props.mk)

            return {
                players,
                player
            }
        },
        data: () => ({
            loadCount: 0,
            loaded: false
        }),
        computed: {
            URLS: () => URLS,
            renderSongName: () => (song) => {
                let songName = song.name.split('.')
                if (songName.length === 1) {
                    return song.name
                } else {
                    return song.name.split('.').slice(0, -1).join('.')
                }
            },
            musicSource: () => (sample) => {
                if (sample instanceof File) {
                    return URL.createObjectURL(sample)
                } else {
                    return sample.url
                }
            },
            sampleKey: () => (sample) => {
                if (sample instanceof File) {
                    return sample.lastModified
                } else {
                    return sample.url.split('/').pop()
                }
            },
            lastModified: () => (sample, indx) => {
                if (sample.lastModified) {
                    return `${sample.lastModified}${indx + 1}`
                } else {
                    return `${new Date().valueOf()}${indx + 1}`
                }
            },
            trackNumber: () => (indx) => {
                const trackNum = (indx + 1).toString()

                return `Track ${('00' + trackNum).substring(trackNum.length)}`
            }
        },
        watch: {
            songs: {
                handler: function (songs) {
                    this.loaded = false

                    if (this.player && this.player.length > songs.length) {
                        this.removeDiscardedSongs()
                    }
                },
                immediate: true
            },
            loadCount: function (count) {
                if (count === this.songs.length && !this.loaded) {
                    this.initPlayer()
                } else if (count > this.songs.length) {
                    this.loadCount = this.songs.length
                } else if (count < 0) {
                    this.loadCount = 0
                }
            },
            player: function (player) {
                player.forEach((song, indx) => {
                    song.ref = this.getAudioRef(indx)
                })
            }
        },
        mounted() {
            this.globalPause()
        },
        methods: {
            getAudioRef(indx) {
                return this.$refs['sample-music'].find(
                    (el) => parseInt(el.getAttribute('data-key')) === indx
                )
            },
            initPlayer() {
                for (let i = 0; i < this.songs.length; i++) {
                    let sample = this.songs[i]

                    if (this.player[i]) {
                        continue
                    }

                    if (sample instanceof File) {
                        this.player.push({
                            name: sample.name,
                            type: 'file',
                            playing: false,
                            showMenu: false,
                            editing: false,
                            newName: '',
                            lastModified: sample.lastModified,
                            src: this.musicSource(sample),
                            duration: 0,
                            progressInterval: null,
                            progress: 0
                        })
                    } else {
                        this.player.push({
                            name: sample.title,
                            type: 'object',
                            playing: false,
                            showMenu: false,
                            editing: false,
                            newName: '',
                            lastModified: new Date().valueOf(),
                            src: this.musicSource(sample),
                            duration: 0,
                            progressInterval: null,
                            progress: 0
                        })
                    }
                }

                this.loaded = true
            },
            crossBrowserCompatibleSources(sample, indx) {
                const audio = this.getAudioRef(indx)
                const source = document.createElement('source')
                source.src = this.musicSource(sample)
                source.type = 'audio/mpeg'
                audio.appendChild(source)
                audio.preload = 'metadata'
            },
            handleLoadMusic(sample, indx) {
                this.crossBrowserCompatibleSources(sample, indx)
                this.loadCount = this.loadCount + 1
            },
            handleAddDuration() {
                for (let i = 0; i < this.player.length; i++) {
                    let ref = this.getAudioRef(i)
                    let sample = this.player[i]

                    sample.duration = this.convertToMinutes(ref.duration)
                }
            },
            globalPause() {
                Object.keys(this.players).forEach((key) => {
                    this.players[key].value.forEach((song) => {
                        this.handlePause(song)
                    })
                })
            },
            handlePlay(song, indx) {
                this.globalPause()

                let ref = this.getAudioRef(indx)
                song.playing = true
                this.handleSongProgress(ref, song)
                this.$nextTick(() => {
                    song.ref.play()
                })
            },
            handleSongProgress(ref, song) {
                song.progressInterval = setInterval(() => {
                    const length = ref.duration
                    const progress = Math.ceil((ref.currentTime / length) * 100)

                    song.progress = progress
                }, 100)
            },
            handlePause(song) {
                song.playing = false
                clearInterval(song.progressInterval)
                song.ref.pause()
            },
            handleChangeName(song, indx) {
                let newSongs = [...this.songs]

                if (!song.newName) {
                    song.editing = false
                    return
                }

                if (newSongs[indx] instanceof File) {
                    let extension = newSongs[indx].name.split('.').pop()
                    song.name = `${song.newName}.${extension}`

                    let newUploadedSong = new File([newSongs[indx]], song.name, {
                        type: newSongs[indx].type
                    })

                    newSongs.splice(indx, 1, newUploadedSong)
                } else {
                    song.name = song.newName

                    newSongs[indx].title = song.newName

                    let titles = this.handleNewTitle(newSongs)
                    this.$emit('sync-title-change', {
                        old_audio_titles: JSON.stringify(titles)
                    })
                }

                this.$emit('update:songs', newSongs)
                this.$emit('new-songs', newSongs)
                this.resetState()
            },
            handleNewTitle(songs) {
                return songs.reduce(
                    (a, c) => ({
                        ...a,
                        [c.id]: c.title
                    }),
                    {}
                )
            },
            handleClickRemove(indx) {
                this.loadCount = this.loadCount - 1

                if (!(this.songs[indx] instanceof File)) {
                    this.$emit('sync-song-delete', [this.songs[indx].name_file])
                }

                let newSongs = [...this.songs]
                newSongs.splice(indx, 1)
                this.player.splice(indx, 1)
                this.$emit('update:songs', newSongs)
                this.$emit('new-songs', newSongs)
                this.resetState()
            },
            handleClickMove(song, indx) {
                let oldIndex = indx
                let newIndex = indx - 1
                let uploadedSong = this.songs[indx]

                if (newIndex < 0) {
                    newIndex = this.player.length - 1
                }

                let newSongs = [...this.songs]
                this.player.splice(oldIndex, 1)
                this.player.splice(newIndex, 0, song)

                newSongs.splice(oldIndex, 1)
                newSongs.splice(newIndex, 0, uploadedSong)
                this.$emit('update:songs', newSongs)
                this.resetState()
            },
            handleClickEdit(song) {
                song.editing = true
                song.showMenu = false
            },
            handleToggleMenu(song) {
                if (song.showMenu) {
                    song.showMenu = false
                } else {
                    this.player.forEach((s) => (s.showMenu = false))
                    song.showMenu = true
                }
            },
            removeDiscardedSongs() {
                let indxToDel = []

                this.player.forEach((p, i) => {
                    if (p.type === 'file') {
                        indxToDel.push(i)
                    }
                })

                indxToDel.forEach((i) => {
                    this.player.splice(i, 1)
                })
            },
            convertToMinutes(seconds) {
                let finalSeconds = Math.floor(seconds % 60)

                if (finalSeconds >= 0 && finalSeconds < 10) {
                    finalSeconds = `0${finalSeconds}`
                }
                return `${(seconds / 60) | 0}:${finalSeconds}`
            },
            resetState() {
                for (let i = 0; i < this.player.length; i++) {
                    let song = this.player[i]

                    song.playing = false
                    song.showMenu = false
                    song.editing = false
                    song.newName = ''
                }
            }
        }
    }
</script>
<style lang="scss" scoped>
    $smallMobile: 350px;
    $mobileMax: 600px;

    @media screen and (max-width: $smallMobile) {
        .player-wrapper {
            max-width: 270px !important;
        }
        .progress {
            width: 200px !important;
        }
    }
    @media screen and (max-width: $mobileMax) and (min-width: $smallMobile) {
        .player-wrapper {
            max-width: 100% !important;
        }
        .progress {
            width: 249px !important;
        }
    }

    .music-player-container {
        overflow-x: scroll;
        -ms-overflow-style: none;
        scrollbar-width: none;

        p {
            margin-bottom: 0px;
        }
        button {
            border: none;
        }
        .player-container {
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;

            .player-wrapper {
                padding: 30px 0px;
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
                align-items: center;
                max-width: 603px;
                width: 80vw;
                position: relative;
                border-top: 1px solid rgba(255, 255, 255, 0.14);
                border-bottom: 1px solid rgba(255, 255, 255, 0.14);

                .play-action {
                    margin-right: 6px;

                    .play-btn,
                    .pause-btn {
                        border-radius: 50%;
                        width: 30px;
                        height: 30px;
                        background: #acacac;

                        img {
                            border-radius: 100px;
                            border: none;
                        }
                    }
                }

                .song-info {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: flex-start;
                    gap: 5px;

                    .progress {
                        border-radius: 8px;
                        max-width: 509px;
                        min-width: 200px;
                        width: 100%;
                        height: 4px;
                        background: #646464;

                        .progress-bar {
                            background-color: #dedede !important;
                        }
                    }

                    p {
                        color: #acacac;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 550;
                        line-height: 125%;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    input {
                        padding: 12px 16px 16px 16px;
                        border-radius: 12px;
                        border: 1px solid #8b8b8b;
                        background: #262626;
                        color: #646464;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                    }

                    input::placeholder {
                        color: #646464;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                    }

                    button {
                        padding: 11px 30px 12px 30px;
                        border-radius: 100px;
                        background: #cdeb09;
                        color: black;
                        font-family: Instrument Sans;
                        font-size: 18px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 120%;
                    }
                }

                .kebab-menu {
                    margin-left: auto;

                    button {
                        background: none;
                    }
                }

                .music-menu {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    background: #262626;
                    gap: 5px;
                    border-radius: 5px;
                    padding: 10px 15px 10px 15px;
                    position: absolute;
                    right: 30px;
                    top: 10px;
                    z-index: 2;

                    button {
                        background: none;
                        color: #acacac;
                        font-family: Instrument Sans;
                        font-size: 14px;
                        font-style: normal;
                        font-weight: 550;
                        line-height: 125%;
                    }

                    .menu-divider {
                        background: rgba(255, 255, 255, 0.14);
                        height: 1px;
                        width: 100%;
                    }
                }
            }
        }
    }
    .music-player-container::-webkit-scrollbar {
        display: none;
    }
</style>
